/**
 * The hover toolbar that floats over a message — V9-01 §2.
 *
 * ## What it replaces
 *
 * The actions used to be **inline siblings of the bubble** inside the message
 * row, hidden with `opacity-0`. Opacity hides ink, not layout: the buttons held
 * their width at all times, so every received bubble sat a toolbar's width away
 * from its avatar — the "hand-width gap" in the founder's screenshot (§1) — and
 * every reveal shoved the message sideways as tooltips and the reaction picker
 * changed the row's measurements mid-hover.
 *
 * Both symptoms were one mistake. The toolbar is `position: absolute` now,
 * anchored to the bubble's top corner and half-overlapping its top edge, the
 * way Slack and Teams do it. It is out of flow, so the bubble's bounding box is
 * *identical* hovered and not — which is what the Playwright test asserts,
 * because "no layout shift" is a measurement, not an opinion.
 *
 * ## Two behaviours worth naming
 *
 * - **It flips below near the top of the pane.** A toolbar hanging half above
 *   the first visible message is clipped by the scroll container, so the top
 *   row of a conversation would be the one row you cannot act on.
 * - **Long-press opens the same actions as a sheet.** There is no hover on a
 *   phone, and an always-visible toolbar on a 360px pane costs more room than
 *   the message. Same children, so the two can never offer different actions.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** How close to the pane's top edge before the toolbar hangs below instead. */
const FLIP_THRESHOLD_PX = 36;
/** Long enough not to fire on a tap, short enough to feel deliberate. */
const LONG_PRESS_MS = 450;

/**
 * Press-and-hold, for touch only.
 *
 * A mouse gets the hover toolbar; firing this for a slow click as well would
 * open a sheet over the toolbar somebody was already reaching for.
 */
export function useLongPress(onLongPress: () => void): {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
} {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  return {
    onPointerDown: (event) => {
      if (event.pointerType === 'mouse') return;
      cancel();
      timer.current = setTimeout(onLongPress, LONG_PRESS_MS);
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    // Otherwise a long press on iOS raises the browser's own menu over ours.
    onContextMenu: (event) => {
      if (window.matchMedia('(pointer: coarse)').matches) event.preventDefault();
    },
  };
}

export function MessageToolbar({
  align,
  sheetOpen,
  onSheetOpenChange,
  children,
}: {
  /**
   * Kept for symmetry with the row, but both sides hang from the top-*right*
   * corner, exactly as §2 says.
   *
   * The first version mirrored an own message's toolbar to the left, reasoning
   * that its right corner is the pane's edge. That was wrong in a way only a
   * measurement catches: an overlay wider than a short bubble then extends past
   * the pane and the *document* gains 115px of horizontal scroll at tablet
   * width — V8-02's "no dead gutter" test found it. Anchored right, a wide
   * toolbar overhangs inward, over the empty middle, which costs nothing.
   */
  align: 'left' | 'right';
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [below, setBelow] = useState(false);

  /**
   * Measured on reveal rather than on scroll: it is one rect read at the moment
   * the pointer arrives, instead of a listener on a list that can hold hundreds
   * of rows.
   */
  const place = useCallback(() => {
    const node = ref.current;
    const pane = node?.closest('[data-testid="chat-thread"]');
    if (!node || !pane) return;
    setBelow(
      node.getBoundingClientRect().top - pane.getBoundingClientRect().top < FLIP_THRESHOLD_PX,
    );
  }, []);

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={place}
        onFocus={place}
        data-testid="chat-message-actions"
        data-placement={below ? 'below' : 'above'}
        className={cn(
          // Out of flow, so revealing it cannot move anything.
          'absolute right-1 z-20 flex items-center gap-0.5 rounded-[8px] border border-[var(--border)] vt-surface px-0.5 shadow-[var(--elevation)]',
          'opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
          /**
           * Hidden only where there is genuinely no hover — a real touch
           * device, which reaches the same actions by long press. Keyed on
           * `hover: none` rather than on width, because a narrow *desktop*
           * window still has a pointer, and hiding by breakpoint left a 360px
           * browser with no way to open a thread at all.
           */
          '[@media(hover:none)]:hidden',
          below ? 'bottom-0 translate-y-1/2' : 'top-0 -translate-y-1/2',
        )}
        data-align={align}
      >
        {children}
      </div>

      {/* Touch — the same children, so the two can never drift apart. */}
      <Dialog open={sheetOpen} onOpenChange={onSheetOpenChange}>
        <DialogContent data-testid="chat-message-sheet" className="max-w-[320px]">
          <DialogTitle className="mb-2 text-[13px]">Message actions</DialogTitle>
          <div
            className="flex flex-wrap items-center gap-1"
            onClick={() => onSheetOpenChange(false)}
          >
            {children}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
